import EmblaCarousel from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import { select, addClass, removeClass, hasClass, getData } from 'helpers/dom'

const selectors = {
  VIEWPORT_SELECTOR: '.js-embla-viewport',
  DOTS_SELECTOR: '.js-embla-dots',
  DOT_ITEM_CLASS: 'embla-dot',
  DOT_ITEM_SELECTED_CLASS: 'embla-dot-selected',
  PREV_BUTTON_SELECTOR: '.js-embla-button-prev',
  NEXT_BUTTON_SELECTOR: '.js-embla-button-next',
  TOGGLE_BUTTON_SELECTOR: '.js-embla-button-play-stop',
  ZOOM_SELECTOR: '.js-zoom',
  ZOOM_ACTIVE_CLASS: 'zoom-active',
  VIDEO_SELECTOR: 'video[autoplay]'
}

class EmblaInstance {
  constructor(element, options, plugins) {
    this.carousel = element
    this.isCustomDrag = getData('is-custom-drag', element)
    this.viewport = select(selectors.VIEWPORT_SELECTOR, this.carousel)
    this.dots = select(selectors.DOTS_SELECTOR, this.carousel)
    this.nextButton = select(selectors.NEXT_BUTTON_SELECTOR, this.carousel)
    this.prevButton = select(selectors.PREV_BUTTON_SELECTOR, this.carousel)
    this.toggleButton = select(selectors.TOGGLE_BUTTON_SELECTOR, this.carousel)
    this.options = options || { align: 'start', loop: false }
    this.plugins = plugins.autoplay ? [Autoplay({ delay: Number(plugins.autoplay), stopOnInteraction: false })] : []

    this.autoplayPlugin = this.plugins.length ? this.plugins[0] : null
    this.isAutoplayActive = !!plugins.autoplay

    if (this.isCustomDrag === 'true') {
      this.options.watchDrag = this.callbackWatchDrag
    }

    this.emblaApi = EmblaCarousel(this.viewport, this.options, this.plugins)

    this.removeButtonsHandle = this.buttonsClickHandle(this.emblaApi, this.prevButton, this.nextButton, this.onButtonClick)
    this.removeDotsHandle = this.dotsClickHandle(this.emblaApi, this.dots, this.onButtonClick)

    this.emblaApi.on('destroy', this.removeButtonsHandle)
    this.emblaApi.on('destroy', this.removeDotsHandle)

    this.emblaApi.on('scroll', () => this.stopAutoplayOnInteraction())
    this.emblaApi.on('select', () => this.stopAutoplayOnInteraction())

    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', () => this.toggleAutoplay(), false)
    }
  }

  onButtonClick(emblaApi) {
    const { autoplay } = emblaApi.plugins()

    if (!autoplay) return
    if (autoplay.options.stopOnInteraction !== false) autoplay.stop()
  }

  toggleAutoplay() {
    if (!this.autoplayPlugin || !this.emblaApi) return

    if (this.isAutoplayActive) {
      this.autoplayPlugin.stop()
    } else {
      this.autoplayPlugin.play()
    }

    this.isAutoplayActive = !this.isAutoplayActive

    const playIcon = select('.js-embla-play-icon', this.carousel)
    const pauseIcon = select('.js-embla-pause-icon', this.carousel)

    if (playIcon && pauseIcon) {
      playIcon.classList.toggle('hidden', this.isAutoplayActive)
      pauseIcon.classList.toggle('hidden', !this.isAutoplayActive)
    }
  }

  callbackWatchDrag(emblaApi) {
    const slideIndex = emblaApi.slidesInView()
    const currentSlide = emblaApi.slideNodes()[slideIndex]
    const zoomEl = select(selectors.ZOOM_SELECTOR, currentSlide)

    return !(zoomEl && hasClass(selectors.ZOOM_ACTIVE_CLASS, zoomEl))
  }

  toggleButtonsState(emblaApi, prevButton, nextButton) {
    const buttonStateHandle = () => {
      if (emblaApi.canScrollPrev()) prevButton.removeAttribute('disabled')
      else prevButton.setAttribute('disabled', 'disabled')

      if (emblaApi.canScrollNext()) nextButton.removeAttribute('disabled')
      else nextButton.setAttribute('disabled', 'disabled')
    }

    emblaApi.on('select', buttonStateHandle).on('init', buttonStateHandle).on('reInit', buttonStateHandle)

    return () => {
      prevButton.removeAttribute('disabled')
      nextButton.removeAttribute('disabled')
    }
  }

  stopAutoplayOnInteraction() {
    if (this.autoplayPlugin && !this.isAutoplayActive) {
      this.autoplayPlugin.stop()
    }

    const currentSlide = this.emblaApi.slideNodes()[this.emblaApi.selectedScrollSnap()]
    const videoEl = select(selectors.VIDEO_SELECTOR, currentSlide)

    if (videoEl) {
      videoEl.play()
    }
  }

  buttonsClickHandle(emblaApi, prevButton, nextButton, onButtonClick) {
    const scrollPrev = () => {
      emblaApi.scrollPrev()
      if (onButtonClick) onButtonClick(emblaApi)
    }

    const scrollNext = () => {
      emblaApi.scrollNext()
      if (onButtonClick) onButtonClick(emblaApi)
    }

    prevButton.addEventListener('click', scrollPrev, false)
    nextButton.addEventListener('click', scrollNext, false)

    const buttonActiveHandle = this.toggleButtonsState(emblaApi, prevButton, nextButton)

    return () => {
      buttonActiveHandle()
      prevButton.removeEventListener('click', scrollPrev, false)
      nextButton.removeEventListener('click', scrollNext, false)
    }
  }

  dotsClickHandle(emblaApi, dots, onButtonClick) {
    let dotNodes = []

    if (dots === null) return false

    const buttonClickHandle = () => {
      dots.innerHTML = emblaApi
        .scrollSnapList()
        .map(() => `<button class="${selectors.DOT_ITEM_CLASS}" type="button" aria-label="dot"><span class="hidden"></span></button>`)
        .join('')

      const scrollTo = (index) => {
        emblaApi.scrollTo(index)
        if (onButtonClick) onButtonClick(emblaApi)
      }

      dotNodes = Array.from(dots.querySelectorAll(`.${selectors.DOT_ITEM_CLASS}`))
      dotNodes.forEach((dotNode, index) => {
        dotNode.addEventListener('click', () => scrollTo(index), false)
      })
    }

    const dotsActiveHandle = () => {
      const previous = emblaApi.previousScrollSnap()
      const selected = emblaApi.selectedScrollSnap()

      if (!dotNodes.length) return false

      removeClass(selectors.DOT_ITEM_SELECTED_CLASS, dotNodes[previous])
      addClass(selectors.DOT_ITEM_SELECTED_CLASS, dotNodes[selected])
    }

    emblaApi
      .on('init', buttonClickHandle)
      .on('reInit', buttonClickHandle)
      .on('init', dotsActiveHandle)
      .on('reInit', dotsActiveHandle)
      .on('select', dotsActiveHandle)

    return () => {
      dots.innerHTML = ''
    }
  }
}

export { EmblaInstance }
